import DocumentActionsSection from 'components/common/documents-upload/DocumentActionsSection';
import ProposalStatusLayout from 'components/common/proposal/ProposalStatusLayout';
import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { DocumentList } from 'components/common/documents-upload/DocumentsList';
import RequiredDocumentsPageStore, {
  RequiredDocumentsPageStoreContext,
  useRequiredDocumentsPageStore,
} from 'store/pages/required-documents/pageStore';
import { useDocumentsUploadUrlParams } from 'global/hooks/useDocumentsUploadUrlParams';
import { useHistory } from 'react-router-dom';
import { CARD_DATA } from 'global/checklists-helpers/ChecklistHelper';
import { DocumentsBodyLayout } from 'components/common/documents-upload/DocumentsBodyLayout';
import { PageFooter, PageHeader } from '@insurancemenu/imenu-components-lib';
import { PageWrapperStyled } from './styles';

const PageContent: React.FC = observer(() => {
  const pageStore = useRequiredDocumentsPageStore();
  const { periodId } = useDocumentsUploadUrlParams();
  const history = useHistory();

  useEffect(() => {
    void pageStore.loadInitialData();
  }, []);

  const onBack = (): void => {
    history.push(`/checklist/${periodId}`);
  };

  const onStepComplete = async (): Promise<void> => {
    await pageStore.completeStep();
    history.push(`/checklist/${periodId}`);
  };

  return (
    <PageWrapperStyled>
      <PageHeader
        title={CARD_DATA.REQUIRED_DOCUMENTS.title}
        subtitle={CARD_DATA.REQUIRED_DOCUMENTS.text}
        onBackButtonClick={onBack}
        subtitleMaxWidth="51rem"
        spacing="withActionsBelow"
      />
      <DocumentsBodyLayout
        actionsSection={
          <DocumentActionsSection
            requestDocumentModalStore={pageStore.requestDocumentModalStore}
            uploadDocumentModalStore={pageStore.uploadDocumentModalStore}
            loading={pageStore.isPageLoading}
            disableMutativeActions={pageStore.thisStepHasBeenCompleted}
          />
        }
        documentsList={
          <DocumentList
            activeDocuments={pageStore.activeDocuments}
            inactiveDocuments={pageStore.inactiveDocuments}
            loading={pageStore.isPageLoading}
            disableMutativeActions={pageStore.thisStepHasBeenCompleted}
          />
        }
        uncompleteStep={pageStore.uncompleteStep}
        stepIsCompleted={pageStore.thisStepHasBeenCompleted && !pageStore.isPageLoading}
        hasOneOrMoreDocuments={pageStore.activeDocuments.length > 0}
      />
      <PageFooter
        onComplete={onStepComplete}
        onBack={onBack}
        disableCompleteButton={
          pageStore.thisStepHasBeenCompleted || pageStore.isPageLoading || !pageStore.pageDomainCompletion.allowed
        }
        completeDisabledReason={
          pageStore.thisStepHasBeenCompleted || pageStore.isPageLoading
            ? ''
            : pageStore.pageDomainCompletion.notAllowedMessage
        }
      />
    </PageWrapperStyled>
  );
});

const RequiredDocumentsPage: React.FC = () => {
  const { periodId } = useDocumentsUploadUrlParams();

  const pageStore = useMemo(() => new RequiredDocumentsPageStore({ entityId: periodId }), []);
  return (
    <ProposalStatusLayout>
      <RequiredDocumentsPageStoreContext.Provider value={pageStore}>
        <PageContent />
      </RequiredDocumentsPageStoreContext.Provider>
    </ProposalStatusLayout>
  );
};

export default RequiredDocumentsPage;
